import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { isSelectableHubScriptSetId } from "../../../lib/hub-script-target.js";

export interface HubScriptSetOption {
  id: string;
  name: string;
  type?: "PERSONA" | "COMPETITOR" | "USER_GENERATED" | "POST_FLIGHT";
}

export interface HubScriptSetPickerProps {
  sets: HubScriptSetOption[];
  value: string;
  onValueChange: (setId: string) => void;
  onCreateSet?: () => void;
  isCreatingSet?: boolean;
}

/** Set target inside Create Script and Import Scripts when the hub has no single set. */
export function HubScriptSetPicker({
  sets,
  value,
  onValueChange,
  onCreateSet,
  isCreatingSet = false,
}: HubScriptSetPickerProps) {
  const { t } = useTranslation();
  const needsSet = !isSelectableHubScriptSetId(value);

  return (
    <div className="flex flex-col gap-1.5 border-b border-border/60 px-6 py-4">
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <label className="mb-1.5 block text-sm font-medium text-foreground" htmlFor="hub-script-set">
            {t("personaOpportunities.hub.actions.setLabel", "Opportunity set")}
          </label>
          <Select value={value} onValueChange={onValueChange} disabled={isCreatingSet}>
            <SelectTrigger id="hub-script-set" className="w-full">
              <SelectValue
                placeholder={t("personaOpportunities.hub.actions.setPlaceholder", "Select a set")}
              />
            </SelectTrigger>
            <SelectContent>
              {sets.length === 0 ? (
                <p className="px-2 py-1.5 text-xs text-muted-foreground">
                  {t(
                    "personaOpportunities.hub.actions.setGroupYoursEmpty",
                    "No sets of your own yet. Use Create Set to add one.",
                  )}
                </p>
              ) : (
                sets.map((set) => (
                  <SelectItem
                    key={set.id}
                    value={set.id}
                    className="data-[state=checked]:bg-primary-subtle data-[state=checked]:font-medium data-[state=checked]:text-primary-subtle-foreground"
                  >
                    {set.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
        {onCreateSet ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconStart={isCreatingSet ? undefined : "plus"}
            disabled={isCreatingSet}
            onClick={onCreateSet}
          >
            {isCreatingSet
              ? t("common.creating", "Creating…")
              : t("personaOpportunities.hub.actions.createSet", "Create Set")}
          </Button>
        ) : null}
      </div>
      {needsSet ? (
        <p className="text-xs text-muted-foreground">
          {t(
            "personaOpportunities.hub.actions.setRequired",
            "Choose a set before continuing.",
          )}
        </p>
      ) : null}
    </div>
  );
}
